export default class Modals {

  private allModals: NodeListOf<HTMLElement>
  private allTogglers: NodeListOf<HTMLElement>
  private allCloseButtons: NodeListOf<HTMLElement>
  private options: Options
  private activeModal: string
  private wrapperClasses: string[] = []
  private savedScrollY: number | null = null
  
  constructor(options?: any) {
    this.resetOptions(options)
    this.resetModals()
    this.selectModals()
    this.attachListeners()
  }

  public closeAll(): void {
    this.resetModals()
    this.restoreBody()
    this.hideBackdrop()
    this.resetAllWrappers()
  }

  private attachListeners(): void {
    this.allTogglers.forEach(toggler => {
      toggler.addEventListener('click', ({ target }) => {
        // tslint:disable-next-line
        const { dataset } = target

        this.showModal(dataset.modalTarget)
        this.showBackdrop()
        this.modifyBody()

        if (dataset.modalWrapper) {
          const { modalWrapper } = dataset
          
          this.modifyWrapper(modalWrapper)
          this.wrapperClasses.push(modalWrapper)
        }  

        if(dataset.modalAnchor) {
          const { modalAnchor, modalTarget } = dataset

          this.scrollToAnchor(modalAnchor, modalTarget)
        }
      })
    })
    
    this.allCloseButtons.forEach(toggler => {
      toggler.addEventListener('click', e => {
        this.closeAll()
      })
    })
  }

  private get hasBackdrop(): boolean {
    const { backdropSelector } = this.options

    return document.querySelector(backdropSelector) ? true : false;
  }

  private showBackdrop(): void {
    const { backdropSelector, backdropClass } = this.options

    if (this.hasBackdrop) {
      document.querySelector(backdropSelector)?.classList.add(backdropClass)
    }
  }

  private hideBackdrop(): void {
    const { backdropSelector, backdropClass } = this.options

    if (this.hasBackdrop) {
      document.querySelector(backdropSelector)?.classList.remove(backdropClass)
    }
  }

  private modifyWrapper(selector: string): void {
    const { wrapperClass } = this.options
    const wrapper = document.querySelector(selector)

    if (wrapper) {
      wrapper.classList.add(wrapperClass)
    }
  }

  private resetAllWrappers(): void {
    const { wrapperClass } = this.options

    this.wrapperClasses.forEach(selector => {
      const wrapper = document.querySelector(selector)

      if (wrapper) {
        wrapper.classList.remove(wrapperClass)
      }
    })
  }

  private selectModals() {
    this.allModals = document.querySelectorAll(this.options.modalSelector)
    this.allTogglers = document.querySelectorAll(this.options.togglerSelector)
    this.allCloseButtons = document.querySelectorAll(this.options.closeButtonSelector)
  }

  private showModal(id: string): void {
    const { modalClass } = this.options

    this.activeModal = id
    document.getElementById(id)?.classList.add(modalClass)
  }

  private restoreBody(): void {
    const { bodyClass } = this.options

    document.body.classList.remove(bodyClass)

    // restore scroll position if we saved one
    if (this.savedScrollY !== null) {
      document.body.style.top = ''
      window.scrollTo(0, this.savedScrollY)
      this.savedScrollY = null
    }
  }
  
  private modifyBody(): void {
    const { bodyClass } = this.options

    // save current scroll position and lock body to avoid jump-to-top
    this.savedScrollY = window.scrollY || window.pageYOffset || 0
    document.body.style.top = `-${this.savedScrollY}px`

    // add body class without overwriting other classes
    document.body.classList.add(bodyClass)
  }

  private resetModals(): void {
    const { modalClass } = this.options

    if (this.activeModal) {
      document.getElementById(this.activeModal)?.classList.remove(modalClass)
    }
    this.activeModal = null
  }

  private resetOptions(options: any): void {
    this.options = {
      ...{
        modalSelector: '[data-modal]',
        togglerSelector: '[data-modal-target]',
        closeButtonSelector: '[data-modal-close]',
        backdropSelector: '[data-modal-backdrop]',
        modalClass: '--active',
        bodyClass: '--no-scroll',
        backdropClass: '--active',
        wrapperClass: '--shift',
      },
      ...options
    }
  }

  private scrollToAnchor(anchor, parent) {
    const parentElement = document.getElementById(parent)
    const scrollTo = parentElement?.querySelector(`[id="${anchor}"]`)

    scrollTo?.scrollIntoView()
  }
}

interface Options {
  modalSelector: string 
  togglerSelector: string
  closeButtonSelector: string
  backdropSelector: string
  modalClass: string
  bodyClass: string
  backdropClass: string
  wrapperClass: string
}